import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "motion/react";
import newsletters from "./newsletterData";
import blogs from "./blogData";
import "../Styles/Newsletter.css";
import "../Styles/Animations.css";
import { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

export default function Newsletters() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim() && email.includes("@")) {
      setSubscribed(true);
      setEmail("");
      setTimeout(() => {
        setSubscribed(false);
      }, 6000);
    }
  };

  // Sort newsletters by date descending
  const sortedNews = [...newsletters].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Extract featured items
  const mainFeatured = sortedNews[0];
  const sideFeatured = sortedNews.slice(1, 4);
  const listNews = sortedNews.slice(4);

  // Extract latest 5 blogs for the sidebar links
  const latestBlogs = [...blogs]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 3);
  };

  return (
    <>
      <Helmet>
        <title>Immigration Newsletters & Study Abroad Resources | VE Abroad</title>
        <meta name="description" content="Stay updated with the latest news, visa policy changes, and expert study abroad insights. Subscribe to Vidya Education Abroad newsletters today." />
        <link rel="canonical" href="https://veabroad.com/newsletters" />
      </Helmet>

      {/* Hero Header */}
      <section className="newsletter-hero position-relative" style={{ padding: "180px 0 80px" }}>
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ width: "450px", height: "450px", top: "-150px", right: "-100px" }}></div>
          <div className="va-blob va-blob-teal" style={{ width: "350px", height: "350px", bottom: "-80px", left: "-80px" }}></div>
        </div>

        <div className="container text-center position-relative z-1">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="newsletter-hero-badge mb-3 d-inline-block text-white px-3 py-1.5 rounded-pill uppercase">
              VE Abroad Knowledge Hub
            </span>
            <h1 className="display-4 fw-bold text-white mb-3">
              Immigration & Study{" "}
              <span className="va-shine-text">
                Newsletters
              </span>
            </h1>
            <p className="lead text-white-50 mx-auto" style={{ maxWidth: "650px" }}>
              Get policy breakdowns, study abroad strategies, and global visa alerts directly from Vidya Education Abroad experts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Top Featured Section */}
      <div className="container newsletter-featured-container">
        <div className="newsletter-grid">
          {/* Main Featured (Left) */}
          {mainFeatured && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Link to={`/newsletter/${mainFeatured.slug}`} className="newsletter-card-large va-hover-lift text-decoration-none">
                <div className="newsletter-img-container">
                  <img src={mainFeatured.image} alt="" className="newsletter-zoom-img" loading="eager" />
                </div>
                <div className="newsletter-overlay">
                  <span className="newsletter-badge">{mainFeatured.category}</span>
                  <h2 className="newsletter-overlay-title">{mainFeatured.title}</h2>
                  <div className="newsletter-meta">
                    <i className="bi bi-calendar3 me-1"></i> {mainFeatured.date} • Featured Resource
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {/* Side Featured (Right - Stack of 3) */}
          <motion.div 
            className="newsletter-small-col"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {sideFeatured.map((item, idx) => (
              <Link to={`/newsletter/${item.slug}`} className="newsletter-card-small va-hover-lift text-decoration-none" key={item.slug}>
                <div className="newsletter-img-container">
                  <img src={item.image} alt="" className="newsletter-zoom-img" loading="lazy" />
                </div>
                <div className="newsletter-overlay">
                  <h3 className="newsletter-overlay-title">{item.title}</h3>
                  <div className="newsletter-meta" style={{ fontSize: "0.8rem" }}>
                    <span style={{ color: "var(--accent)", fontWeight: "750", textTransform: "uppercase", marginRight: "8px" }}>{item.category}</span>
                    <i className="bi bi-calendar3 me-1"></i> {item.date}
                  </div>
                </div>
              </Link>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Main Body Section */}
      <section className="container py-5 mt-4">
        <div className="row g-5">
          {/* Left Column: Remaining Newsletters List */}
          <div className="col-lg-8">
            <h3 className="mb-4 fw-bold text-dark border-bottom pb-2">
              <i className="bi bi-journal-text text-teal me-2" style={{ color: "#36948c" }}></i>
              Recent Editions
            </h3>

            {listNews.length === 0 ? (
              <div className="text-center py-5 bg-white rounded-3 shadow-sm border border-light">
                <i className="bi bi-info-circle text-muted fs-2 mb-2 d-block"></i>
                <p className="text-muted">No additional newsletters available. Check back soon!</p>
              </div>
            ) : (
              <>
                <RevealGroup as="div" className="news-list-items" stagger={0.08} amount={0.1}>
                  {listNews.slice(0, visibleCount).map((item) => (
                    <RevealItem
                      key={item.slug}
                      direction="up"
                      className="news-item-row-card va-hover-lift"
                    >
                      <div className="news-item-thumb">
                        <img src={item.image} alt="" loading="lazy" />
                      </div>
                      <div className="news-item-content">
                        <div className="news-meta-row mb-2">
                          <span className="news-meta-cat">{item.category}</span>
                          <span>•</span>
                          <span>{item.date}</span>
                        </div>
                        <Link to={`/newsletter/${item.slug}`} className="text-decoration-none">
                          <h4 className="news-item-title">{item.title}</h4>
                        </Link>
                        <p className="news-item-excerpt line-clamp-2">{item.excerpt}</p>
                        <Link to={`/newsletter/${item.slug}`} className="text-teal fw-semibold text-decoration-none mt-auto" style={{ color: "#36948c" }}>
                          Read Newsletter <i className="bi bi-arrow-right ms-1"></i>
                        </Link>
                      </div>
                    </RevealItem>
                  ))}
                </RevealGroup>

                {visibleCount < listNews.length && (
                  <div className="text-center mt-4 pt-2">
                    <MagneticButton as="span" className="d-inline-block">
                      <button className="premium-btn px-4 py-2.5 text-white va-shimmer-btn" onClick={handleLoadMore}>
                        Load Older Editions
                      </button>
                    </MagneticButton>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Column: Sidebar Widgets */}
          <div className="col-lg-4">
            <div className="newsletter-sidebar">
              
              {/* Subscribe Widget */}
              <div className="newsletter-widget widget-subscribe">
                <h4 className="text-white fw-bold mb-2">Subscribe to VE Abroad</h4>
                <p className="text-white-50 small mb-3">
                  Receive real-time visa updates, study guides, and scholarship notifications direct to your inbox.
                </p>
                
                <AnimatePresence mode="wait">
                  {!subscribed ? (
                    <motion.form 
                      onSubmit={handleSubscribe} 
                      className="subscribe-input-group"
                      key="sub-form"
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <input
                        type="email"
                        required
                        className="form-control subscribe-input"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      <MagneticButton as="span" style={{ display: "flex", width: "100%" }}>
                        <button type="submit" className="subscribe-btn mt-1 va-shimmer-btn" style={{ width: "100%" }}>
                          Subscribe Now <i className="bi bi-send ms-1"></i>
                        </button>
                      </MagneticButton>
                    </motion.form>
                  ) : (
                    <motion.div
                      key="sub-success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                      className="p-3 bg-white bg-opacity-10 rounded-3 border border-success border-opacity-20 text-center mt-3"
                    >
                      <i className="bi bi-check-circle-fill text-success fs-3 mb-2 d-block" style={{ color: "#25d366" }}></i>
                      <h5 className="text-white fw-bold mb-1">You're Subscribed!</h5>
                      <p className="text-white-50 small mb-0">
                        Thank you for joining. You will receive our next newsletter issue soon.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Quick Recent Feed Widget */}
              <div className="newsletter-widget">
                <h4 className="widget-title">Quick Updates</h4>
                <div className="recent-feed-list">
                  {sortedNews.slice(0, 4).map((item) => (
                    <Link to={`/newsletter/${item.slug}`} className="recent-feed-item" key={item.slug}>
                      <img src={item.image} alt="" className="recent-feed-img" loading="lazy" />
                      <div className="recent-feed-info">
                        <span className="recent-feed-date">{item.date}</span>
                        <h5 className="recent-feed-title">{item.title}</h5>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Related Blog Links (User requested) */}
              <div className="newsletter-widget">
                <h4 className="widget-title">Latest Blog Guides</h4>
                <div className="d-flex flex-column gap-2 mt-2">
                  {latestBlogs.map((blog) => (
                    <Link to={`/blog/${blog.slug}`} className="blog-link-item" key={blog.slug}>
                      <span className="text-truncate me-2" style={{ maxWidth: "230px" }}>{blog.title}</span>
                      <i className="bi bi-chevron-right blog-link-icon"></i>
                    </Link>
                  ))}
                </div>
                <div className="text-center mt-3 pt-2">
                  <Link to="/blog" className="text-teal small fw-bold text-decoration-none" style={{ color: "#36948c" }}>
                    Visit Our Full Blog <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
}
